import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import style from './CardAprobar.module.css';
import { useDispatch } from 'react-redux';
import { deleteProperty, diffuseProperty } from '../../Redux/Actions/actions';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import ApprovalActions from '../ApprovalActions/ApprovalActions';
import VersionHistory from '../VersionHistory/VersionHistory';
import PropertyComments from '../PropertyComments/PropertyComments';
import PropertyPreviewModal from '../PropertyPreviewModal/PropertyPreviewModal';

const ESTADO_REVISION_BADGE = {
  'sin-doc': { label: 'Documentación pendiente', dot: '🔴', className: 'badgeSinDoc' },
  'solo-doc': { label: 'Doc. aprobada · pendiente de alta', dot: '🟡', className: 'badgeSoloDoc' },
  alta: { label: 'Publicada', dot: '🟢', className: 'badgeAlta' },
};

const CardAprobar = ({ property, onRefresh }) => {
  const dispatch = useDispatch();
  const [showPasswordInput, setShowPasswordInput] = useState(false);
  const [password, setPassword] = useState("");
  const [activeTab, setActiveTab] = useState(null); // 'versions' | 'comments' | null
  const [showPreview, setShowPreview] = useState(false);
  const [showDiffusionConfirm, setShowDiffusionConfirm] = useState(false);
  const [diffusing, setDiffusing] = useState(false);
  const [diffusionResult, setDiffusionResult] = useState(null); // { type: 'ok'|'error', message }
  const [diffusionProgress, setDiffusionProgress] = useState(0); // 0-100
  const progressTimer = useRef(null);

  // Limpia el intervalo del progreso al desmontar.
  useEffect(() => () => clearInterval(progressTimer.current), []);

  // Anima la barra hasta ~90% mientras se espera la respuesta del servidor.
  const startProgress = () => {
    setDiffusionProgress(8);
    clearInterval(progressTimer.current);
    progressTimer.current = setInterval(() => {
      setDiffusionProgress((p) => {
        if (p >= 90) return p;
        const step = p < 50 ? 7 : p < 75 ? 3 : 1.5;
        return Math.min(90, p + step);
      });
    }, 180);
  };

  // Completa la barra al 100% y la oculta tras un instante.
  const finishProgress = () => {
    clearInterval(progressTimer.current);
    setDiffusionProgress(100);
    setTimeout(() => setDiffusionProgress(0), 1000);
  };

  const handleDelete = () => {
    const confirmed = window.confirm('¿Estás seguro de que deseas eliminar esta propiedad? Esta acción no se puede deshacer.');
    if (confirmed) {
      dispatch(deleteProperty(property.id))
        .then(() => {
          window.location.reload();
        })
        .catch((error) => {
          console.error('Error al eliminar la propiedad:', error);
        });
    }
  };

  const handleDownloadClick = () => {
    setShowPasswordInput(true);
  };

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
  };

  const handleDownloadDocuments = async () => {
    if (password !== "byraices123") {
      alert("Contraseña incorrecta. Inténtalo de nuevo.");
      return;
    }

    const zip = new JSZip();
    const folder = zip.folder(`Documentos_${property.id}`);

    if (!folder) {
      alert("Error al crear la carpeta ZIP.");
      return;
    }

    const fetchPromises = property.documentation.map(async (fileUrl) => {
      try {
        const response = await fetch(fileUrl);
        const blob = await response.blob();
        const fileName = fileUrl.split('/').pop();
        folder.file(fileName, blob);
      } catch (error) {
        console.error(`Error descargando ${fileUrl}:`, error);
      }
    });

    await Promise.all(fetchPromises);

    zip.generateAsync({ type: "blob" }).then((content) => {
      saveAs(content, `Documentos_${property.id}.zip`);
    });

    alert("Descarga completada.");
    setPassword("");
    setShowPasswordInput(false);
  };

  const hasDocumentation = property.documentation?.length > 0;

  const getDaysPublished = () => {
    if (!property.createdAt) return "Fecha desconocida";
    const createdAtDate = new Date(property.createdAt);
    const currentDate = new Date();
    const differenceInTime = currentDate.getTime() - createdAtDate.getTime();
    const differenceInDays = Math.floor(differenceInTime / (1000 * 3600 * 24));
    return differenceInDays === 1 ? "1 día" : `${differenceInDays} días`;
  };

  const getFormattedDate = (dateString) => {
    if (!dateString) return "Fecha desconocida";
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString("es-ES", options);
  };

  const toggleTab = (tab) => {
    setActiveTab(activeTab === tab ? null : tab);
  };

  // Sólo se puede difundir una propiedad publicada.
  const isPublished = property.estadoRevision === 'alta' || property.statusProperty === true;

  const handleConfirmDiffusion = async () => {
    setDiffusing(true);
    setDiffusionResult(null);
    startProgress();
    try {
      const data = await dispatch(diffuseProperty(property.id));
      const total = data?.total ?? 0;
      const sent = data?.sent ?? 0;
      const failed = data?.failed ?? 0;
      setDiffusionResult({
        type: failed > 0 ? 'warn' : 'ok',
        message: `Enviado a ${sent} de ${total} agente(s).${failed > 0 ? ` ${failed} fallaron.` : ''}`,
      });
      setShowDiffusionConfirm(false);
    } catch (error) {
      const status = error.response?.status;
      const messages = {
        409: 'La propiedad no está publicada, no se puede difundir.',
        422: 'No hay agentes con mail válido para enviar.',
        404: 'La propiedad no existe.',
        400: 'ID de propiedad inválido.',
        401: 'Clave de difusión incorrecta.',
        503: 'La difusión no está configurada en el servidor.',
        502: 'Todos los envíos fallaron. Reintentá más tarde.',
      };
      setDiffusionResult({
        type: 'error',
        message: messages[status] || (error.response?.data?.message || 'Error al enviar la difusión.'),
      });
    } finally {
      finishProgress();
      setDiffusing(false);
    }
  };

  return (
    <div className={`${style.card} ${!hasDocumentation ? style.borderOrange : ''}`}>
      {/* Cabecera con imagen y datos principales */}
      <div className={style.cardTop}>
        <div className={style.imageContainer}>
          {property?.photo?.length > 0 && (
            <img src={property.photo[0]} className={style.image} alt={property.title} />
          )}
          {!hasDocumentation && (
            <span className={style.docWarning}>Sin documentación</span>
          )}
        </div>

        <div className={style.mainInfo}>
          <div className={style.titleRow}>
            <h5 className={style.cardTitle}>{property.title}</h5>
            <span className={style.propertyId}>#{property.id}</span>
          </div>

          {property.estadoRevision && ESTADO_REVISION_BADGE[property.estadoRevision] && (
            <span className={`${style.estadoBadge} ${style[ESTADO_REVISION_BADGE[property.estadoRevision].className]}`}>
              {ESTADO_REVISION_BADGE[property.estadoRevision].dot} {ESTADO_REVISION_BADGE[property.estadoRevision].label}
            </span>
          )}

          <div className={style.priceRow}>
            <span className={style.price}>{property.currency} ${property.price}</span>
            {property.expenses && (
              <span className={style.expenses}>+ ${property.expenses} expensas</span>
            )}
          </div>

          <p className={style.cardText}>{property.description}</p>

          <div className={style.metaRow}>
            <span className={style.dateInfo}>
              {getFormattedDate(property.createdAt)} · {getDaysPublished()} publicado
            </span>
          </div>

          {(property.ownerName || property.ownerPhone || property.ownerEmail) && (
            <div className={style.ownerInfo}>
              <span className={style.ownerLabel}>Propietario:</span>
              {property.ownerName && <span>{property.ownerName}</span>}
              {property.ownerPhone && <span className={style.ownerDetail}>📞 {property.ownerPhone}</span>}
              {property.ownerEmail && <span className={style.ownerDetail}>✉ {property.ownerEmail}</span>}
            </div>
          )}
        </div>
      </div>

      {/* Barra de acciones rápidas */}
      <div className={style.quickActions}>
        <button className={style.actionBtn} onClick={() => setShowPreview(true)}>
          👁 Vista previa
        </button>
        <Link to={`https://byraices.com/detail/${property.id}`} className={style.actionLink}>
          <button className={style.actionBtnEdit}>🌐 Ver en sitio</button>
        </Link>
        <Link to={`/detalles/${property.id}`} className={style.actionLink}>
          <button className={style.actionBtnEdit}>✎ Editar</button>
        </Link>
        {hasDocumentation && (
          <button onClick={handleDownloadClick} className={style.actionBtnDoc}>
            📄 Documentación
          </button>
        )}
        {isPublished && (
          <button
            onClick={() => { setDiffusionResult(null); setShowDiffusionConfirm(true); }}
            className={style.actionBtnDiffuse}
            disabled={diffusing}
          >
            📢 Difundir a agentes
          </button>
        )}
        <button onClick={handleDelete} className={style.actionBtnDelete}>
          🗑 Eliminar
        </button>
      </div>

      {(diffusing || diffusionProgress > 0) && (
        <div className={style.progressWrap}>
          <div className={style.progressBar}>
            <div
              className={`${style.progressFill} ${diffusionProgress >= 100 ? style.progressComplete : ''}`}
              style={{ width: `${diffusionProgress}%` }}
            />
          </div>
          <span className={style.progressLabel}>
            {diffusionProgress >= 100
              ? '✅ ¡Listo! 100%'
              : `Enviando… ${Math.round(diffusionProgress)}%`}
          </span>
        </div>
      )}

      {showDiffusionConfirm && (
        <div className={style.diffusionContainer}>
          <p className={style.diffusionText}>
            Se enviará un mail con esta propiedad a todos los agentes activos. ¿Confirmás el envío?
          </p>
          <div className={style.diffusionActions}>
            <button
              onClick={handleConfirmDiffusion}
              className={style.confirmButton}
              disabled={diffusing}
            >
              {diffusing ? 'Enviando...' : 'Confirmar envío'}
            </button>
            <button
              onClick={() => setShowDiffusionConfirm(false)}
              className={style.cancelPassBtn}
              disabled={diffusing}
            >
              Cancelar
            </button>
          </div>
        </div>
      )}

      {diffusionResult && (
        <div
          className={`${style.diffusionResult} ${
            diffusionResult.type === 'error'
              ? style.diffusionError
              : diffusionResult.type === 'warn'
              ? style.diffusionWarn
              : style.diffusionOk
          }`}
        >
          {diffusionResult.message}
        </div>
      )}

      {showPasswordInput && (
        <div className={style.passwordContainer}>
          <input
            type="password"
            placeholder="Ingrese contraseña"
            value={password}
            onChange={handlePasswordChange}
            className={style.passwordInput}
          />
          <button onClick={handleDownloadDocuments} className={style.confirmButton}>
            Confirmar descarga
          </button>
          <button onClick={() => setShowPasswordInput(false)} className={style.cancelPassBtn}>
            Cancelar
          </button>
        </div>
      )}

      {/* Sección de aprobación */}
      <div className={style.approvalSection}>
        <ApprovalActions property={property} onRefresh={onRefresh} />
      </div>

      {/* Tabs de Versiones / Comentarios */}
      <div className={style.tabsContainer}>
        <div className={style.tabBar}>
          <button
            className={`${style.tab} ${activeTab === 'versions' ? style.tabActive : ''}`}
            onClick={() => toggleTab('versions')}
          >
            📋 Versiones
          </button>
          <button
            className={`${style.tab} ${activeTab === 'comments' ? style.tabActive : ''}`}
            onClick={() => toggleTab('comments')}
          >
            💬 Comentarios
          </button>
        </div>

        {activeTab === 'versions' && (
          <div className={style.tabContent}>
            <VersionHistory propertyId={property.id} />
          </div>
        )}
        {activeTab === 'comments' && (
          <div className={style.tabContent}>
            <PropertyComments propertyId={property.id} />
          </div>
        )}
      </div>

      {showPreview && (
        <PropertyPreviewModal
          propertyId={property.id}
          onClose={() => setShowPreview(false)}
          onSaved={onRefresh}
        />
      )}
    </div>
  );
};

export default CardAprobar;
